import { useState, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { PermissionGuard } from "@/components/Auth"
import { EditSaveButtons } from "@/components"
import {
  ClientChannel,
  ClientChannelType,
  isInstagramClientConfig,
  Workspace,
  Permission,
  isMessengerClientConfig,
  isWebClientConfig,
  isWidgetClientConfig,
  isExtensionClientConfig,
  isSlackClientConfig,
  isLineClientConfig,
  ClientChannelConfig,
} from "@/types"
import { useToast } from "@/hooks"
import {
  FetchStatus,
  useUpdateClientChannelApi,
  clientChannelResponseToClientChannel,
  makeUpdateClientChannelRequest,
} from "@/hooks/api"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"

import {
  WebConfigFields,
  WidgetConfigFields,
  ExtensionConfigFields,
  SlackConfigFields,
  LineConfigFields,
  MetaConfigFields,
} from "./configs"

export interface ChannelConfigFormProps {
  channel: ClientChannel
  workspace: Workspace
  onChannelUpdated?: (channel: ClientChannel) => void
  onUnsavedChangesChange?: (hasChanges: boolean) => void
}

export const ChannelConfigForm = ({
  channel,
  workspace,
  onChannelUpdated,
  onUnsavedChangesChange,
}: ChannelConfigFormProps) => {
  const { t } = useTranslation()
  const { successToast, alertToast } = useToast()

  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState(channel.config)
  const [hasManagedConfigUnsavedChanges, setHasManagedConfigUnsavedChanges] =
    useState(false)
  const hasConfigValueChanges = useMemo(
    () => JSON.stringify(formData) !== JSON.stringify(channel.config),
    [formData, channel.config]
  )
  const hasUnsavedChanges =
    hasManagedConfigUnsavedChanges || hasConfigValueChanges

  // APIs
  const {
    data: updateChannelRespData,
    error: updateChannelRespError,
    status: updateChannelRespStatus,
    fetchFn: updateChannel,
  } = useUpdateClientChannelApi(
    workspace.organizationId || "",
    workspace.id,
    channel.id
  )

  useEffect(() => {
    setIsEditing(false)
    setHasManagedConfigUnsavedChanges(false)
  }, [channel.id])

  useEffect(() => {
    if (isEditing) return
    setFormData(channel.config)
  }, [channel.config, isEditing])

  useEffect(() => {
    onUnsavedChangesChange?.(hasUnsavedChanges)
  }, [hasUnsavedChanges, onUnsavedChangesChange])

  const handleSave = (configOverride?: ClientChannelConfig) => {
    try {
      const configToSave = configOverride || formData
      const request = makeUpdateClientChannelRequest(
        undefined,
        undefined,
        configToSave
      )
      updateChannel({
        jsonBody: request,
      })
    } catch (error) {
      alertToast({
        title: t("workspace.channels.messages.error.updateConfig.title"),
        description: t(
          "workspace.channels.messages.error.updateConfig.invalidType"
        ),
      })
      return
    }
  }

  // Handle update success
  useEffect(() => {
    if (!updateChannelRespData || !updateChannelRespData.data) return
    const updatedChannel = clientChannelResponseToClientChannel(
      updateChannelRespData.data
    )
    onChannelUpdated?.(updatedChannel)
    setIsEditing(false)
    successToast({
      title: t("workspace.channels.messages.configUpdated.title"),
      description: t("workspace.channels.messages.configUpdated.description"),
    })
  }, [updateChannelRespData, successToast, onChannelUpdated, t])

  // Handle update error
  useEffect(() => {
    if (!updateChannelRespError) return
    alertToast({
      title: t("workspace.channels.messages.error.updateConfig.title"),
      description: getUserFacingApiErrorMessage(
        updateChannelRespError,
        t,
        t("workspace.channels.messages.error.updateConfig.description")
      ),
    })
  }, [updateChannelRespError, alertToast, t])

  const handleCancel = () => {
    setFormData(channel.config)
  }

  const handleConfigChange = (partialConfig: Partial<typeof formData>) => {
    setFormData(
      (prevConfig) =>
        ({
          ...prevConfig,
          ...partialConfig,
        }) as ClientChannelConfig
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">
          {channel.type === ClientChannelType.WEB
            ? t("workspace.channels.config.webTitle")
            : channel.type === ClientChannelType.WIDGET
              ? t("workspace.channels.config.widgetTitle")
              : channel.type === ClientChannelType.SLACK
                ? t("workspace.channels.config.slackTitle")
                : channel.type === ClientChannelType.LINE
                  ? t("workspace.channels.config.lineTitle")
                  : channel.type === ClientChannelType.MESSENGER
                    ? t("workspace.channels.config.messengerTitle")
                    : channel.type === ClientChannelType.INSTAGRAM
                      ? t("workspace.channels.config.instagramTitle")
                      : t("workspace.channels.config.extensionTitle")}
        </h3>
        {/* Messaging channels manage their own edit/save flow via dedicated forms */}
        {channel.type !== ClientChannelType.SLACK &&
          channel.type !== ClientChannelType.LINE &&
          channel.type !== ClientChannelType.MESSENGER &&
          channel.type !== ClientChannelType.INSTAGRAM && (
            <PermissionGuard
              permission={Permission.WsUpdateResources}
              workspace={workspace}
              disabled
            >
              <EditSaveButtons
                size="sm"
                editText={t("workspace.channels.config.actions.edit")}
                saveText={t("workspace.channels.config.actions.save")}
                cancelText={t("workspace.channels.config.actions.cancel")}
                isLoading={updateChannelRespStatus === FetchStatus.Loading}
                onSave={handleSave}
                onCancel={handleCancel}
                onEditingChange={setIsEditing}
                disabled={updateChannelRespStatus === FetchStatus.Loading}
              />
            </PermissionGuard>
          )}
      </div>

      <div className="space-y-6">
        {/* Web-specific fields */}
        {isWebClientConfig(formData) && (
          <WebConfigFields
            config={formData}
            isEditing={isEditing}
            workspace={workspace}
            channel={channel}
            onConfigChange={handleConfigChange}
          />
        )}

        {isWidgetClientConfig(formData) && (
          <WidgetConfigFields
            config={formData}
            isEditing={isEditing}
            workspace={workspace}
            channel={channel}
            onConfigChange={handleConfigChange}
            onChannelUpdated={onChannelUpdated}
          />
        )}

        {/* Extension-specific fields */}
        {isExtensionClientConfig(formData) && (
          <ExtensionConfigFields
            config={formData}
            isEditing={isEditing}
            onConfigChange={handleConfigChange}
          />
        )}

        {/* Slack-specific fields */}
        {isSlackClientConfig(formData) && (
          <PermissionGuard
            permission={Permission.WsUpdateResources}
            workspace={workspace}
            disabled
          >
            <SlackConfigFields
              config={formData}
              isEditing={isEditing}
              channelId={channel.id}
              workspaceId={workspace.id}
              organizationId={workspace.organizationId || ""}
              onConfigChange={handleConfigChange}
              onSave={handleSave}
              isLoading={updateChannelRespStatus === FetchStatus.Loading}
              onDirtyChange={setHasManagedConfigUnsavedChanges}
            />
          </PermissionGuard>
        )}

        {/* LINE-specific fields */}
        {isLineClientConfig(formData) && (
          <PermissionGuard
            permission={Permission.WsUpdateResources}
            workspace={workspace}
            disabled
          >
            <LineConfigFields
              config={formData}
              isEditing={isEditing}
              channelId={channel.id}
              workspaceId={workspace.id}
              organizationId={workspace.organizationId || ""}
              onConfigChange={handleConfigChange}
              onSave={handleSave}
              isLoading={updateChannelRespStatus === FetchStatus.Loading}
              onDirtyChange={setHasManagedConfigUnsavedChanges}
            />
          </PermissionGuard>
        )}

        {isMessengerClientConfig(formData) && (
          <PermissionGuard
            permission={Permission.WsUpdateResources}
            workspace={workspace}
            disabled
          >
            <MetaConfigFields
              channelType={ClientChannelType.MESSENGER}
              config={formData}
              channelId={channel.id}
              workspaceId={workspace.id}
              organizationId={workspace.organizationId || ""}
              onConfigChange={handleConfigChange}
              onSave={handleSave}
              isLoading={updateChannelRespStatus === FetchStatus.Loading}
              onDirtyChange={setHasManagedConfigUnsavedChanges}
            />
          </PermissionGuard>
        )}

        {isInstagramClientConfig(formData) && (
          <PermissionGuard
            permission={Permission.WsUpdateResources}
            workspace={workspace}
            disabled
          >
            <MetaConfigFields
              channelType={ClientChannelType.INSTAGRAM}
              config={formData}
              channelId={channel.id}
              workspaceId={workspace.id}
              organizationId={workspace.organizationId || ""}
              onConfigChange={handleConfigChange}
              onSave={handleSave}
              isLoading={updateChannelRespStatus === FetchStatus.Loading}
              onDirtyChange={setHasManagedConfigUnsavedChanges}
            />
          </PermissionGuard>
        )}
      </div>
    </div>
  )
}

export default ChannelConfigForm
